import React, { ChangeEvent, Fragment, MouseEvent } from "react";
import { RouteComponentPropsHistory } from "../../types";

import withAuth from "../withAuth";
import withTitle from "../withTitle";
import { formatDateLocal, angleToDirection, formatDateTimeDurationLong } from "../../Utilities";

import { V2 } from "../../Api";
import Indicator from "../Indicator";
import DelayInput from "../DelayInput";
import ViewToolbar from "../ViewToolbar";
import { Link } from "react-router-dom";
import { IAuthProps } from "../AuthContext";
import HasRight from "../HasRight";

interface VehicleListViewState {
    isLoading: boolean | string;
    isLoadingLight: boolean;
    canEdit: boolean;

    vehicles: any[];

    searchText: string;
    searchStatus: string;
    searchCheckedOutStatus: string;

    sortColumn: string;
    sortDirection: "asc" | "desc";
}

class VehicleListView extends React.Component<RouteComponentPropsHistory<any, any> & IAuthProps, VehicleListViewState> {
    constructor(props: Readonly<RouteComponentPropsHistory<any, any> & IAuthProps>) {
        super(props);

        const state: VehicleListViewState = {
            isLoading: true,
            isLoadingLight: false,
            canEdit: props.authContext.hasRight("veh:write"),

            vehicles: [],

            searchText: "",
            searchStatus: "enabled",
            searchCheckedOutStatus: "",
            sortColumn: "tag",
            sortDirection: "asc",
        };

        if (props.location.state) {
            const prev = props.location.state;

            state.searchText = prev.searchText;
            state.searchStatus = prev.searchStatus;
            state.searchCheckedOutStatus = prev.searchCheckedOutStatus;
            state.sortColumn = prev.sortColumn;
            state.sortDirection = prev.sortDirection;
        }

        this.state = state;

        this.handleElementChange = this.handleElementChange.bind(this);
        this.openTrackerWindow = this.openTrackerWindow.bind(this);
    }

    public async componentDidMount() {
        this.search();
    }

    public render() {
        return (
            <div className="viewer-root">
                <ViewToolbar>
                    <button type="button" className={`icon-refresh ${this.state.isLoading ? " anim-spin" : ""}`} onClick={() => this.search(true)} title="Refresh" />
                    <button type="button" className="icon-map" onClick={this.openTrackerWindow} title="Open Samsara..." />
                    <HasRight right="veh:create">
                        <Link to="/vehicles/new" className="icon-plus" />
                    </HasRight>
                </ViewToolbar>
                <div className="filter-control-box">
                    <DelayInput timeout={300} type="search" className="fill-space" name="searchText" value={this.state.searchText} onChangeDelayed={this.handleElementChange} placeholder="Search name or tag..." autoComplete="off_please_chrome" />
                    <select name="searchCheckedOutStatus" value={this.state.searchCheckedOutStatus} onChange={this.handleElementChange} disabled={!!this.state.searchText}>
                        <option value="">Checked in & out</option>
                        <option value="available">Available</option>
                        <option value="checked_out">Checked out</option>
                    </select>
                    <select name="searchStatus" value={this.state.searchStatus} onChange={this.handleElementChange} disabled={!!this.state.searchText}>
                        <option value="">All Status</option>
                        <option value="enabled">Enabled</option>
                        <option value="disabled">Hidden</option>
                    </select>
                </div>
                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th className="clickable" onClick={() => this.sortByField("tag")} title="Sort by tag">Tag {this.sortIcon("tag")}</th>
                            <th className="clickable" onClick={() => this.sortByField("name")} title="Sort by description">Description {this.sortIcon("name")}</th>
                            <HasRight right="veh:tracker:read">
                                <th className="col-smallest" />
                                <th className="col-smallest clickable" onClick={() => this.sortByField("lastseen")} title="Sort by last seen near">Last Seen Near {this.sortIcon("lastseen")}</th>
                            </HasRight>
                            <th className="clickable" onClick={() => this.sortByField("checked_out")} title="Sort by checked out date" colSpan={2}>Checked out {this.sortIcon("checked_out")}</th>
                            <th className="clickable" onClick={() => this.sortByField("vehicleStatus")} title="Sort by status">Status {this.sortIcon("vehicleStatus")}</th>
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr>
                            <th>Vehicles</th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.state.isLoading === true && this.state.isLoadingLight === false
                            ? <tr><td colSpan={10}><div className="spinner spinner-center lds-dual-ring" /></td></tr>
                            : typeof this.state.isLoading === "string"
                                ? <tr><td colSpan={10} className="wrap"><span className="error-text">{this.state.isLoading}</span></td></tr>
                                : null
                        }
                        {this.state.vehicles.map(a => <tr key={a.id} className={this.state.canEdit ? "clickable" : ""} onClick={() => this.handleRowClick(a.id)}>
                            <td className="col-smallest text-mono col-text-larger col-bold inline" data-caption="Tag: ">{a.tag}</td>
                            <td className="wrap" data-caption="Name: ">{a.name}<HasRight right="veh:tracker:read">{a.tracker && (a.tracker.lastOdometer || a.tracker.lastFuel) ? <Fragment><br /><small style={{ fontStyle: "italic" }}>{a.tracker.lastOdometer ? `${Math.round(a.tracker.lastOdometer).toLocaleString()} miles${a.tracker?.lastFuel ? " | " : ""}` : ""}{a.tracker?.lastFuel && <><i className="icon-local_gas_station" style={{fontSize: "1.5em"}} title="Fuel %" />{a.tracker.lastFuel} %</>}</small></Fragment> : null}</HasRight></td>
                            <HasRight right="veh:tracker:read">
                                <td></td>
                                <td data-caption="Last Seen: ">
                                    {this.vehicleTrackerToIndicator(a.tracker)}
                                    {this.vehicleTrackerToBatteryIndicator(a.tracker)}
                                </td>
                            </HasRight>
                            <td className="col-smallest inline" data-caption="Checked out: ">{a.status === 2 ? a.keys[0].user.name : <Indicator color="green" text="Available" />}</td>
                            <td className="col-smallest">{a.status === 2 ? formatDateLocal(a.keys[0].timestamp) : null}</td>
                            <td className="col-smallest col-center inline">{<Indicator color={a.vehicleStatus === 1 ? "green" : "yellow"} text={a.vehicleStatus === 1 ? "Enabled" : "Hidden"} />}</td>
                        </tr>)}
                    </tbody>
                </table>
            </div>
        );
    }

    private async search(hideLoading = false) {
        try {
            this.setState({
                isLoading: true,
                isLoadingLight: hideLoading
            });
            this.saveSearchState();

            const parameters = [
                "mode=status"
            ];

            if (this.state.searchText)
                parameters.push(`text=${this.state.searchText}`);

            if (!this.state.searchText) {
                if (this.state.searchCheckedOutStatus)
                    parameters.push(`state=${this.state.searchCheckedOutStatus}`);

                if (this.state.searchStatus)
                    parameters.push(`vehicle_status=${this.state.searchStatus}`);
            }

            const vehicles = await V2.get<any[]>(`vehicles?${parameters.join("&")}`);
            this.sortFieldInner(vehicles, this.state.sortColumn, this.state.sortDirection);

            for (const vehicle of vehicles) {
                if (vehicle.tracker) {
                    vehicle.tracker.lastOdometer = (vehicle.tracker.lastOdometer || 0) + vehicle.tracker.lastTrip;
                }
            }

            this.setState({
                isLoading: false,
                isLoadingLight: false,

                vehicles,
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || e || "Could not load vehicle list"
            });
        }
    }

    private handleElementChange(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            [name]: value
        } as any,
            () => this.search()
        );
    }

    private saveSearchState() {
        const location = this.props.location;
        location.state = {
            searchText: this.state.searchText,
            searchStatus: this.state.searchStatus,
            searchCheckedOutStatus: this.state.searchCheckedOutStatus,
            sortColumn: this.state.sortColumn,
            sortDirection: this.state.sortDirection
        };

        this.props.history.replace(location);
    }

    private handleRowClick(id: string) {
        if (!this.state.canEdit)
            return;

        this.props.history.push(`/vehicles/${id}`);
    }

    private openTrackerWindow() {
        window.open("https://cloud.samsara.com", undefined, "resizable,scrollbars,status");
    }

    private sortIcon(column: string) {
        return this.state.sortColumn === column
            ? <i className={`icon-chevron-${this.state.sortDirection === "asc" ? "up" : "down"} icon-style-smaller`} />
            : undefined
    }

    private sortByField(field: string) {
        const direction = this.state.sortColumn === field && this.state.sortDirection === "asc" ? "desc" : "asc";
        const vehicles = this.sortFieldInner(this.state.vehicles, field, direction);

        this.setState({
            sortColumn: field,
            sortDirection: direction,
            vehicles
        }, this.saveSearchState);
    }

    private sortFieldInner(vehicles: any[], field: string, direction: "asc" | "desc") {
        switch (field) {
            case "checked_out":
                direction === "asc"
                    ? vehicles.sort((a, b) => (a.status === 2 ? a.keys[0].timestamp : "").localeCompare(b.status === 2 ? b.keys[0].timestamp : ""))
                    : vehicles.sort((a, b) => (b.status === 2 ? b.keys[0].timestamp : "").localeCompare(a.status === 2 ? a.keys[0].timestamp : ""));
                break;
            case "tag":
                direction === "asc"
                    ? vehicles.sort((a, b) => (a[field] || "").replace(" ", "").localeCompare((b[field] || "").replace(" ", "")))
                    : vehicles.sort((a, b) => (b[field] || "").replace(" ", "").localeCompare((a[field] || "").replace(" ", "")));
                break;
            case "vehicleStatus":
                direction === "asc"
                    ? vehicles.sort((a, b) => (a[field] || 0) - (b[field] || 0))
                    : vehicles.sort((a, b) => (b[field] || 0) - (a[field] || 0));
                break;
            case "name":
                direction === "asc"
                    ? vehicles.sort((a, b) => (a[field] || "").localeCompare(b[field] || ""))
                    : vehicles.sort((a, b) => (b[field] || "").localeCompare(a[field] || ""));
                break;
            case "lastseen":
                direction === "asc"
                    ? vehicles.sort((a, b) =>
                        VehicleListView.compareSortGeofence(a, b) ||
                        ((a.tracker && a.tracker.geofence && a.tracker.geofence.name) || (a.tracker && a.tracker.lastPosition.x.toString()) || "")
                            .localeCompare((b.tracker && b.tracker.geofence && b.tracker.geofence.name) || (b.tracker && b.tracker.lastPosition.x.toString()) || ""))
                    : vehicles.sort((a, b) =>
                        VehicleListView.compareSortGeofence(b, a) ||
                        ((b.tracker && b.tracker.geofence && b.tracker.geofence.name) || (b.tracker && b.tracker.lastPosition.x.toString()) || "")
                            .localeCompare((a.tracker && a.tracker.geofence && a.tracker.geofence.name) || (a.tracker && a.tracker.lastPosition.x.toString()) || ""));
                break;
            default:
                break;
        }

        return vehicles;
    }

    private trackerLocationToLink(tracker: any) {
        if (!tracker.lastPosition) {
            return "Unknown";
        }

        let displayName = `${tracker.lastPosition.x.toFixed(6)}, ${tracker.lastPosition.y.toFixed(6)}`;
        let bubbleText = "";

        if (tracker.lastAddress && (!tracker.geofence || tracker.geofence.locationId === "0")) {
            displayName = tracker.lastAddress.replace(/, \d+$/, "");
        }
        else if (tracker.geofence) {
            displayName = tracker.geofence.name;
            bubbleText = +tracker.geofence.locationId ? "Office" : "";
        }

        return <a href={`https://maps.google.com/?t=k&q=${tracker.lastPosition.x},${tracker.lastPosition.y}`} onClick={this.openMapsWindow}>
            {bubbleText ? <Indicator type="slim" color="blue" text="Office" /> : null}
            {displayName}
        </a>;
    }

    private openMapsWindow(e: MouseEvent<HTMLAnchorElement>) {
        e.stopPropagation();
        e.preventDefault();

        window.open(e.currentTarget.href, undefined, "resizable,scrollbars,status");
    }

    private vehicleTrackerToIndicator(tracker: any) {
        if (!tracker) {
            return null;
        }

        return tracker.lastIsActive === true && tracker.lastSpeed
            ? <Fragment>{this.trackerLocationToLink(tracker)}<br /><Indicator type="slim" color="green">Moving {angleToDirection(tracker.lastHeading)} @ {tracker.lastSpeed === null ? "?" : Math.round(tracker.lastSpeed)} MPH</Indicator></Fragment>
            : tracker.lastIsActive === true
                ? <Fragment>{this.trackerLocationToLink(tracker)}<br /><Indicator type="slim" color="yellow">Idle for {tracker.lastMovement ? formatDateTimeDurationLong(tracker.lastMovement) : "N/A"}</Indicator></Fragment>
                : <Fragment>{this.trackerLocationToLink(tracker)}<br /><small>Parked for {tracker.lastMovement ? formatDateTimeDurationLong(tracker.lastMovement) : "N/A"}</small></Fragment>
    }

    private vehicleTrackerToBatteryIndicator(tracker: any) {
        if (!tracker) {
            return null;
        }

        return tracker.lastVoltage
            ? tracker.lastVoltage < 11.65
                ? <div><Indicator color="red" type="slim" icon="warning">Battery voltage critical ({tracker.lastVoltage.toFixed(1)} V)</Indicator></div>
                : tracker.lastVoltage <= 11.9
                    ? <div><Indicator color="orange" type="slim" icon="warning">Battery voltage low ({tracker.lastVoltage.toFixed(1)} V)</Indicator></div>
                    : null
            : null;
    }

    private static compareSortGeofence(a: any, b: any) {
        const cmpTracker = !!a.tracker === !!b.tracker
            ? 0
            : !!a.tracker
                ? -1
                : 1;

        const cmpGeofence = !!(a.tracker && a.tracker.geofence) === !!(b.tracker && b.tracker.geofence)
            ? 0
            : !!(a.tracker && a.tracker.geofence)
                ? -1
                : 1;

        return cmpTracker || cmpGeofence;
    }
}

export default withAuth(withTitle("Vehicles", VehicleListView));
